import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Kiosk from './pages/Kiosk';
import SuperAdmin from './pages/SuperAdmin';
import { ThemeProvider } from "./context/ThemeContext";
import { useLanguage } from "./lang/useLanguage";

function getSessionIdentity() {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      return {
        hasToken: false,
        isSuperAdmin: false,
        isImpersonated: false
      };
    }

    const payload = JSON.parse(atob(token.split(".")[1]));
    const isImpersonated =
      !!localStorage.getItem("superadmin_token");

    const isSuperAdmin =
      payload.role === "admin" &&
      payload.sub === "superadmin";

    return {
      hasToken: true,
      isSuperAdmin,
      isImpersonated
    };
  } catch {
    return {
      hasToken: false,
      isSuperAdmin: false,
      isImpersonated: false
    };
  }
}

function TenantRouteGuard() {
  const session = getSessionIdentity();

  if (!session.hasToken) {
    return <Navigate to="/login" replace />;
  }

  /*
   * A real SuperAdmin session must never render tenant
   * /dashboard routes accidentally.
   *
   * During impersonation superadmin_token exists and the
   * tenant Dashboard must remain accessible.
   */
  if (
    session.isSuperAdmin &&
    !session.isImpersonated
  ) {
    return <Navigate to="/superadmin" replace />;
  }

  return <Dashboard />;
}

function SuperAdminRouteGuard() {
  const session = getSessionIdentity();

  if (!session.hasToken) {
    return <Navigate to="/login" replace />;
  }

  /*
   * Impersonated tenant sessions and ordinary tenant users
   * must not render the SuperAdmin UI.
   */
  if (
    !session.isSuperAdmin ||
    session.isImpersonated
  ) {
    return <Navigate to="/dashboard/dashboard" replace />;
  }

  return <SuperAdmin />;
}

function RoleRedirect() {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      return <Navigate to="/login" />;
    }

    const payload = JSON.parse(atob(token.split(".")[1]));

    const isImpersonated = !!localStorage.getItem("superadmin_token");

    if (
      payload.role === "admin" &&
      payload.sub === "superadmin" &&
      !isImpersonated
    ) {
      return <SuperAdmin />;
    }

    return <Navigate to="/dashboard/dashboard" />;
  } catch {
    return <Navigate to="/login" />;
  }
}

const API = `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

// ─── SCROLL TO TOP ────────────────────────────────────────────────────────
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

// ─── AI CHAT WIDGET ───────────────────────────────────────────────────────
function AIChatWidget() {
    const { t, lang } = useLanguage();
    const ai = t.aiAssistant || {};
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState(() => [
        { role: "ai", text: ai.welcome }
    ]);

    useEffect(() => {
        setMessages(prev => {
            if (
                prev.length === 1 &&
                prev[0]?.role === "ai"
            ) {
                return [
                    { ...prev[0], text: ai.welcome }
                ];
            }

            return prev;
        });
    }, [lang, ai.welcome]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [aiOnline, setAiOnline] = useState(false);
    const [listening, setListening] = useState(false);
    const messagesEndRef = useRef(null);
    const recognitionRef = useRef(null);

    useEffect(() => {
        if (!token()) return;
        fetch(`${API}/ai/status`, { headers: headers() })
            .then(r => r.json())
            .then(d => setAiOnline(d.status === "online"))
            .catch(() => setAiOnline(false));
    }, []);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const sendMessage = async (text) => {
        const msg = text || input.trim();
        if (!msg) return;
        setInput("");
        setMessages(prev => [...prev, { role: "user", text: msg }]);
        setLoading(true);
        try {
            const r = await fetch(`${API}/ai/chat`, {
                method: "POST", headers: headers(),
                body: JSON.stringify({ message: msg })
            });
            const d = await r.json();
            if (r.ok) {
                setMessages(prev => [...prev, { role: "ai", text: d.answer }]);
                if (d.navigate) {
                    const pageMap = {
                        dashboard: "/dashboard/dashboard",
                        reports_personnel: "/dashboard/reports",
                        reports_sales: "/dashboard/reports",
                        reports_fire: "/dashboard/reports",
                        reports_pl: "/dashboard/reports",
                        quality: "/dashboard/quality",
                        work_orders: "/dashboard/workorders",
                        stock: "/dashboard/stock",
                        maintenance: "/dashboard/maintenance",
                        machines: "/dashboard/machines",
                        energy: "/dashboard/energy",
                        hr: "/dashboard/hr",
                        purchasing: "/dashboard/purchasing",
                        sales: "/dashboard/sales",
                        ncr: "/dashboard/ncr",
                        kaizen: "/dashboard/kaizen",
                    };
                    const url = pageMap[d.navigate];
                    if (url) {
                        setTimeout(() => {
                            window.location.href = url;
                        }, 1500);
                    }
                }
            } else {
                setMessages(prev => [...prev, { role: "ai", text: `${ai.errorPrefix}: ${d.detail || ai.unknownError}` }]);
            }
        } catch {
            setMessages(prev => [...prev, { role: "ai", text: ai.connectionError }]);
        }
        setLoading(false);
    };

    const startListening = () => {
        if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
            alert(ai.speechUnsupported);
            return;
        }
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = lang === "en" ? "en-US" : "tr-TR";
        recognition.interimResults = false;
        recognition.onresult = (e) => {
            const text = e.results[0][0].transcript;
            setInput(text);
            sendMessage(text);
        };
        recognition.onend = () => setListening(false);
        recognition.onerror = () => setListening(false);
        recognitionRef.current = recognition;
        recognition.start();
        setListening(true);
    };

    if (!token()) return null;

    const quickQuestions = [
        ai.quickOee,
        ai.quickCriticalStock,
        ai.quickScrapRate,
        ai.quickMaintenance,
    ];

    return (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999 }}>
            {open && (
                <div style={{
                    width: 360, height: 500, background: "#0f172a",
                    border: "1px solid #1e3a5f", borderRadius: 16,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                    display: "flex", flexDirection: "column",
                    marginBottom: 12, overflow: "hidden"
                }}>
                    {/* Header */}
                    <div style={{ padding: "14px 16px", background: "linear-gradient(135deg,#1e3a5f,#1e40af)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 20 }}>🤖</span>
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{ai.title}</div>
                                <div style={{ fontSize: 10, color: aiOnline ? "#4ade80" : "#f87171" }}>
                                    {aiOnline ? `● ${ai.online}` : `● ${ai.offline}`}
                                </div>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 18 }}>✕</button>
                    </div>

                    {/* Mesajlar */}
                    <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                        {messages.map((m, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                                <div style={{
                                    maxWidth: "80%", padding: "8px 12px", borderRadius: 12,
                                    background: m.role === "user" ? "#1e40af" : "#1e293b",
                                    color: "#e2e8f0", fontSize: 13, lineHeight: 1.5,
                                    borderBottomRightRadius: m.role === "user" ? 4 : 12,
                                    borderBottomLeftRadius: m.role === "ai" ? 4 : 12,
                                }}>{m.text}</div>
                            </div>
                        ))}
                        {loading && (
                            <div style={{ display: "flex", justifyContent: "flex-start" }}>
                                <div style={{ padding: "8px 12px", borderRadius: 12, background: "#1e293b", color: "#94a3b8", fontSize: 13 }}>
                                    ⏳ {ai.thinking}
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Hızlı sorular */}
                    {messages.length <= 1 && (
                        <div style={{ padding: "0 12px 8px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {quickQuestions.map((q, i) => (
                                <button key={i} onClick={() => sendMessage(q)} style={{
                                    padding: "4px 10px", borderRadius: 20, border: "1px solid #334155",
                                    background: "#1e293b", color: "#94a3b8", cursor: "pointer", fontSize: 11
                                }}>{q}</button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div style={{ padding: 12, borderTop: "1px solid #1e293b", display: "flex", gap: 8 }}>
                        <input
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && sendMessage()}
                            placeholder={ai.placeholder}
                            style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid #334155", background: "#1e293b", color: "#e2e8f0", fontSize: 13, outline: "none" }}
                        />
                        <button onClick={startListening} style={{
                            padding: "8px 10px", borderRadius: 8, border: "none",
                            background: listening ? "#ef4444" : "#334155",
                            color: "#fff", cursor: "pointer", fontSize: 16
                        }}>🎤</button>
                        <button onClick={() => sendMessage()} disabled={loading || !input.trim()} style={{
                            padding: "8px 12px", borderRadius: 8, border: "none",
                            background: input.trim() ? "#1e40af" : "#334155",
                            color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700
                        }}>→</button>
                    </div>
                </div>
            )}

            {/* Toggle butonu */}
            <button onClick={() => setOpen(!open)} style={{
                width: 52, height: 52, borderRadius: "50%", border: "none",
                background: "linear-gradient(135deg,#1e40af,#7c3aed)",
                color: "#fff", cursor: "pointer", fontSize: 22,
                boxShadow: "0 4px 16px rgba(30,64,175,0.5)",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "transform 0.2s",
            }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
            >
                {open ? "✕" : "🤖"}
            </button>
        </div>
    );
}

function App() {
    const token = localStorage.getItem('token');

    return (
        <ThemeProvider>
            <Router>
                <ScrollToTop />
                <AIChatWidget />
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/dashboard/:page" element={<TenantRouteGuard />} />
                    <Route path="/dashboard" element={<TenantRouteGuard />} />
                    <Route path="/" element={token ? <RoleRedirect /> : <Navigate to="/login" />} />
                    <Route path="/kiosk" element={<Kiosk />} />
                    <Route path="/superadmin" element={<SuperAdminRouteGuard />} />
                </Routes>
            </Router>
        </ThemeProvider>
    );
}

export default App;
